import {
  dateToEpochSeconds,
  dateToEpochSecondsNullable,
  epochSecondsToDate,
  epochSecondsToDateNullable,
  stripUndefined,
  z,
  type ZodType,
} from '@customerio/hydra-validation';
import type { Campaign as CampaignModel, operations } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

// Generated query params for the campaigns list op. `state` and `sort` are
// constrained to enums in the spec, so deriving them here keeps the params in
// sync with the API instead of widening back to `string`.
type CampaignsListQuery = NonNullable<
  operations['get-v1-environments-environment_id-campaigns']['parameters']['query']
>;

/**
 * Campaign entity — schemas, type, and entity bundle.
 *
 * **Type derivation, not field redeclaration.** The `Campaign` type is
 * the generated `Campaign` model with date fields swapped for `Date`
 * instances. Adding/removing campaign fields on the backend means
 * regenerating `@customerio/hydra-ui-api` — this file follows
 * automatically.
 *
 * The Zod schemas (`CampaignFromApi`, `CampaignToApi`) declare ONLY the
 * fields that need transformation (epoch seconds ↔ Date). `looseObject`
 * preserves every other field at runtime; the type assertion bridges
 * the schema's loose output type to our `Campaign` shape.
 *
 * If we ever need real validation (a field that's required on the wire
 * but optional in TS, an enum we want to assert at runtime, …), declare
 * it on the schema next to the transforms — adds Zod validation
 * incrementally without forcing every other field to be repeated.
 */

/* ------------------------------------------------------------------------- */
/* Type — derived from the API type                                           */
/* ------------------------------------------------------------------------- */

export type Campaign = Omit<CampaignModel, 'created' | 'updated' | 'first_started'> & {
  created: Date;
  updated: Date;
  first_started: Date | null;
};

/** What `serialize()` accepts and returns — denormalized in, wire-shape out. */
type CampaignWriteInput = Partial<Campaign>;
type CampaignWireWritePayload = Partial<CampaignModel>;

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

/**
 * Wire → entity. `looseObject` preserves all unmentioned fields; we
 * declare only the date transforms. The type assertion bridges Zod's
 * loose-object output (which types unmentioned fields as `unknown`)
 * back to the `Campaign` shape inferred from the generated model.
 *
 * No FK-id coercions here: `tag_ids` and `subscription_topic_id` ship
 * from the wire as strings (`string[]` and `string | null` respectively,
 * per the generated `Campaign`), and every entity in this layer uses string
 * ids — so the wire shape is already the entity shape. The
 * number→string coercion lives on `Tag.id` / `SubscriptionTopic.id`,
 * not on the FK arrays referencing them.
 */
const CampaignFromApi =
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  z.looseObject({
    created: epochSecondsToDate,
    updated: epochSecondsToDate,
    first_started: epochSecondsToDateNullable,
  }) as unknown as ZodType<Campaign, unknown>;

/**
 * Entity → wire. Inverse direction. Strips undefined fields so the API
 * body only contains what the caller set.
 */
const CampaignToApi =
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  z
    .looseObject({
      created: dateToEpochSeconds.optional(),
      updated: dateToEpochSeconds.optional(),
      first_started: dateToEpochSecondsNullable.optional(),
    })
    .transform(stripUndefined) as unknown as ZodType<CampaignWireWritePayload, CampaignWriteInput>;

/* ------------------------------------------------------------------------- */
/* List params + relations                                                    */
/* ------------------------------------------------------------------------- */

export interface CampaignListParams {
  page: number;
  state?: CampaignsListQuery['state'];
  type?: string;
  /** Server-side name search term. */
  search?: string;
  searchTypes?: string[];
  subscriptionTopicId?: number;
  sendToUnsubscribed?: boolean;
  sort?: CampaignsListQuery['sort'];
  asc?: boolean;
}

/** Relation declarations for Campaign. Both kinds: `belongs-to` and `has-many`. */
type CampaignRelations = {
  readonly actions: {
    readonly kind: 'has-many';
    readonly target: 'action';
    readonly via: { readonly on: 'target'; readonly field: 'campaign_id' };
  };
  readonly tags: {
    readonly kind: 'has-many';
    readonly target: 'tag';
    readonly via: { readonly on: 'self'; readonly field: 'tag_ids' };
  };
  readonly subscription_topic: {
    readonly kind: 'belongs-to';
    readonly target: 'subscription_topic';
    readonly via: 'subscription_topic_id';
  };
};

/** @public */
export const campaign = defineEntity<
  Campaign,
  CampaignWireWritePayload,
  CampaignListParams,
  CampaignRelations,
  'workspace'
>({
  name: 'campaign',
  scope: 'workspace',
  pk: (c) => c.id,
  schema: { read: CampaignFromApi, write: CampaignToApi },
  relations: {
    actions: { kind: 'has-many', target: 'action', via: { on: 'target', field: 'campaign_id' } },
    tags: { kind: 'has-many', target: 'tag', via: { on: 'self', field: 'tag_ids' } },
    // The generated Campaign carries `subscription_topic_id?: string | null`
    // — a scalar FK on self, so this is `belongs-to`, not `has-many` (the
    // sideloaded `subscription_topics` on the response is a populator
    // concern, not the relation shape).
    subscription_topic: { kind: 'belongs-to', target: 'subscription_topic', via: 'subscription_topic_id' },
  },
});

export const campaignKeys = campaign.keys;

export function parseCampaign(raw: CampaignModel): Campaign {
  return campaign.parse(raw);
}
